import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/Modal';
import { useSocket } from '../../contexts/SocketContext';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';
import api from '../../utils/api';
import { formatBJ } from '../../utils/datetime';
import { ArrowLeft, Package, FileText, UploadCloud, Trash2, X, Loader, CheckCircle2, AlertTriangle } from 'lucide-react';

// 后台 · 数据包管理（仅管理员）：批量上传多个 .txt（文件名=数据包名，每行一个手机号）。
// 导入时即完成“号码 → 所属包”的归档，这是后续 OCR 匹配的数据基础；所有包进入统一待领取池，用户只能看到“还剩几个包”。
const STATUS = {
  pool:      { label: '待领取', cls: 'badge-info' },
  claimed:   { label: '进行中', cls: 'badge-warning' },
  completed: { label: '已完成', cls: 'badge-success' },
};

export default function PuzzlePackagePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { subscribe } = useSocket();
  const [picked, setPicked] = useState([]);       // 待导入的 File 列表
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);   // 上一次导入结果
  const [list, setList] = useState([]);
  const [pool, setPool] = useState(0);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const fetchList = useCallback(async () => {
    try {
      const res = await api.get('/puzzle/packages');
      setList(res.data.packages || []);
      setPool(res.data.pool_remaining || 0);
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => { if (['puzzle_pool_updated', 'puzzle_session_updated', 'puzzle_updated'].includes(event)) fetchList(); });
  }, [subscribe, fetchList]);

  const handlePick = (e) => {
    const arr = Array.from(e.target.files || []);
    e.target.value = '';
    if (!arr.length) return;
    const ok = arr.filter(f => /\.txt$/i.test(f.name));
    if (ok.length < arr.length) showToast('已过滤非 .txt 文件', 'error');
    setPicked(prev => {
      const names = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...ok.filter(f => !names.has(f.name + f.size))].slice(0, 100);
    });
    setResults(null);
  };

  const handleImport = async () => {
    if (!picked.length) return;
    setImporting(true);
    try {
      const fd = new FormData();
      picked.forEach(f => fd.append('files', f));
      // 中文文件名走单独字段传，避免 multipart 头部编码乱码
      fd.append('names', JSON.stringify(picked.map(f => f.name)));
      const res = await api.post('/puzzle/packages/import', fd, { timeout: 120000 });
      setResults(res.data.results || []);
      setPicked([]);
      showToast(`导入 ${res.data.imported_packages} 个包，共 ${res.data.imported_numbers} 个号码`, res.data.imported_packages ? 'success' : 'error');
      fetchList();
    } catch (err) {
      showToast(err.response?.data?.error || '导入失败', 'error');
    } finally { setImporting(false); }
  };

  const handleDelete = async (p) => {
    const ok = await confirm(`确认删除「${p.name}」？`, { detail: `包内 ${p.total} 个号码将一并删除，仅未被领取过的包可以删除。`, danger: true });
    if (!ok) return;
    try {
      await api.delete(`/puzzle/packages/${p.id}`);
      showToast('已删除', 'success');
      fetchList();
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate('/admin')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}><Package size={16} /> 导入数据</span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div className="card">
          <div onClick={() => inputRef.current?.click()} style={{
            border: '1.5px dashed var(--c-text-tertiary)', borderRadius: 12, padding: '22px 12px', textAlign: 'center', cursor: 'pointer', background: 'var(--c-bg)',
          }}>
            <UploadCloud size={28} color="var(--c-primary)" />
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6 }}>点击选择 .txt 数据包文件</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>支持同时多选，文件名即为数据包名，每行一个手机号</div>
          </div>
          <input ref={inputRef} type="file" accept=".txt,text/plain" multiple style={{ display: 'none' }} onChange={handlePick} />

          {picked.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 6 }}>已选择，待导入：</div>
              {picked.map((f, i) => (
                <div key={f.name + f.size} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--c-bg)', borderRadius: 8, marginBottom: 6 }}>
                  <FileText size={15} color="var(--c-primary)" />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{f.name.replace(/\.txt$/i, '')}</span>
                  <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>{(f.size / 1024).toFixed(1)}KB</span>
                  <button onClick={() => setPicked(p => p.filter((_, j) => j !== i))} disabled={importing}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                    <X size={14} color="var(--c-text-tertiary)" />
                  </button>
                </div>
              ))}
              <button className="btn btn-primary" onClick={handleImport} disabled={importing} style={{ marginTop: 6, height: 46 }}>
                {importing ? <><Loader size={16} className="spin" /> 导入中…</> : `确认导入全部（${picked.length} 个）`}
              </button>
            </div>
          )}

          {results && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 6 }}>导入结果：</div>
              {results.map((r, i) => (
                <div key={i} style={{ padding: '8px 10px', borderRadius: 8, marginBottom: 6, fontSize: 12, lineHeight: 1.6,
                  background: r.imported ? 'rgba(52,199,89,0.08)' : 'rgba(255,59,48,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}>
                    {r.imported ? <CheckCircle2 size={14} color="var(--c-success)" /> : <AlertTriangle size={14} color="var(--c-danger)" />}
                    {r.name}
                  </div>
                  {r.imported > 0
                    ? <span style={{ color: 'var(--c-text-secondary)' }}>
                        导入 {r.imported} 条
                        {r.invalid ? ` · 格式无效 ${r.invalid}` : ''}{r.dupInFile ? ` · 文件内重复 ${r.dupInFile}` : ''}{r.dupExisting ? ` · 与已有包重复 ${r.dupExisting}` : ''}
                      </span>
                    : <span style={{ color: 'var(--c-danger)' }}>{r.error || '没有导入'}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>已导入的数据包</span>
            <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>待领取 <strong style={{ color: 'var(--c-primary)' }}>{pool}</strong> 个</span>
          </div>
          {loading ? <p style={{ textAlign: 'center', padding: 20, color: 'var(--c-text-secondary)' }}>加载中...</p>
            : list.length === 0 ? <p style={{ textAlign: 'center', padding: 20, color: 'var(--c-text-tertiary)', fontSize: 13 }}>还没有导入任何数据包</p>
            : list.map(p => {
              const st = STATUS[p.status] || STATUS.pool;
              return (
                <div key={p.id} style={{ padding: '10px 0', borderBottom: '0.5px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, wordBreak: 'break-all' }}>{p.name}</span>
                      <span className={`badge ${st.cls}`}>{st.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 3 }}>
                      共 {p.total} 条 · 已用 {p.used} · 余 {p.remain}
                      {p.claimed_by ? ` · ${p.claimed_by.name} 领取（计入 ${p.success_count} 单）` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>
                      {p.claimed_at ? `领取于 ${formatBJ(p.claimed_at)}` : `导入于 ${formatBJ(p.created_at)}`}
                    </div>
                  </div>
                  {p.status === 'pool' && (
                    <button onClick={() => handleDelete(p)} style={{ background: 'none', border: '1px solid var(--c-border)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex' }}>
                      <Trash2 size={14} color="var(--c-danger)" />
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      <TabBar mode="admin" />
    </div>
  );
}
